import { Spacing } from '@persona-id/design-tokens';
import { capitalize } from 'lodash/fp';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Permissioned from 'dashboard/components/Permissioned';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { VerificationCertificateKorea } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Sticker from 'design-system/components/Sticker';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const InfoSection = styled('div')`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

interface CertificateKoreaVerificationProps extends VerificationDetailProps {
  verification: VerificationCertificateKorea;
}

const CertificateKoreaVerification = ({ verification }: CertificateKoreaVerificationProps) => {
  const {
    status,
    'name-first': nameFirst,
    'name-last': nameLast,
    birthdate,
    'certificate-type': certificateType,
    'redacted-at': redactedAt,
    'identification-number': identificationNumber,
  } = verification.attributes;

  const redacted = redactedAt != null;
  const showChecks = ![
    VerificationStatus.Initiated,
    VerificationStatus.Confirmed,
    VerificationStatus.Submitted,
    VerificationStatus.Canceled,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ].includes(status);
  const showIdentificationNumber = usePermission(Permission.VerificationGovIdNumberView);

  const checks = verification.attributes.checks;
  // Korean names in Hangul are surname followed by first name with no spaces.
  const nameFull = [nameLast, nameFirst].filter(Boolean).join('');

  return (
    <InfoSection data-test='verification-detail__certificate-korea-verification'>
      <StyledAttributeGrid>
        <Attribute
          label='ID number'
          value={
            <Permissioned
              permission={Permission.VerificationGovIdNumberView}
              fallback={
                <Sticker
                  variant='basic'
                  data-test='verification-detail__certificate-korea-id__not-available'
                >
                  Not Available
                </Sticker>
              }
            >
              {identificationNumber}
            </Permissioned>
          }
          redacted={redacted}
          toggleable={showIdentificationNumber}
        />
        <Attribute label='Full name' value={nameFull} redacted={redacted} />
        <Attribute label='Birthdate' value={formatDate(birthdate)} redacted={redacted} />
        <Attribute
          label='Certificate type'
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value={capitalize(certificateType)}
          redacted={redacted}
          placeholder='No certificate collected'
        />
      </StyledAttributeGrid>
      {showChecks && checks && <ChecksTable checks={checks} />}
    </InfoSection>
  );
};

export default CertificateKoreaVerification;
